/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as S from "./MultiSelectBase.styles";
import * as AFS from "./MultiSelectAdvancedFilter.styles";
import { useMultiSelectPanel } from "./useMultiSelectPanel";
import expandIcon from "../../assets/icons/circlePlus.svg";
import collapseIcon from "../../assets/icons/circleMinus.svg";
import { getResource } from "../../language/resources/getResource";
import {
  DisplayField,
  GroupBy,
  ItemKey,
  NormalizedItem,
  GroupNode,
  Accessor,
  readWithAccessor,
} from "./types";
import { useGroupedTree } from "./useGroupedTree";
import { SelectAllToolbar } from "./SelectAllToolbar";
import { css, SerializedStyles } from "@emotion/react";
import filterIcon from "../../assets/icons/filter.svg";
import { Row } from "../../layout/flexbox";
import { useScopedSelectAll } from "./useScopedSelectAll";
import { useRangeSelection } from "./useRangeSelection";

const iconButtonCss = css`
  cursor: pointer;
  pointer-events: auto;
`;

export type MultiSelectAdvancedFilterProps<T> = {
  resources: Record<string, any>;
  label: string;
  items: T[];

  keyAccessor: Accessor<T, ItemKey>;
  displayFields: DisplayField<T>[];
  groupBy?: GroupBy<T>[];

  selectedKeys: ItemKey[];
  onChange: (keys: ItemKey[]) => void;

  showSearch?: boolean;
  searchPlaceholder?: string;
  /** Render the search field in the control instead of at the top of the panel. */
  searchInControl?: boolean;

  showClear?: boolean;
  clearLabel?: string;

  enableSelectAll?: boolean;
  showSelectAll?: boolean;
  requiredAsterisk?: boolean;

  controlWidth?: number | string;
  controlMaxWidth?: number | string;
  controlHeight?: number | string;
  iconSize?: number | string;

  panelMaxWidth?: number | string;
  panelMaxHeight?: number;

  rootCss?: SerializedStyles;
  buttonCss?: SerializedStyles;
  /** Style overrides for the dropdown panel. */
  panelCss?: SerializedStyles;
  placeholderTextCss?: SerializedStyles;
  selectedTextCss?: SerializedStyles;
  iconCss?: SerializedStyles;
  dropdownIconSrc?: string;

  isItemDisabled?: (item: T) => boolean;
};

export function MultiSelectAdvancedFilter<T>({
  resources,
  label,
  items,
  keyAccessor,
  displayFields,
  groupBy,

  selectedKeys,
  onChange,

  showSearch = true,
  searchPlaceholder = "Search…",
  searchInControl = false,

  showClear = true,
  clearLabel = "Clear",

  enableSelectAll = true,
  showSelectAll = false,
  requiredAsterisk = false,

  controlWidth,
  controlMaxWidth,
  controlHeight,
  iconSize = 24,

  panelMaxWidth,
  panelMaxHeight = 240,

  rootCss,
  buttonCss,
  panelCss,
  placeholderTextCss,
  selectedTextCss,
  iconCss,
  dropdownIconSrc,

  isItemDisabled,
}: MultiSelectAdvancedFilterProps<T>) {
  const { open, setOpen, btnRef, panelRef, panelMinWidth } =
    useMultiSelectPanel();

  const [search, setSearch] = useState("");

  // Reopening starts from the full list.
  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const normalized = useMemo<NormalizedItem<T>[]>(
    () =>
      items.map((item) => {
        const fields = displayFields.map((f) => ({
          value: readWithAccessor(item, f.accessor),
          searchable: f.searchable !== false,
          render: f.render,
        }));

        const disabled = isItemDisabled?.(item) ?? false;

        return {
          key: readWithAccessor(item, keyAccessor),
          fields,
          raw: item,
          disabled,
          searchText: fields
            .filter((f) => f.searchable)
            .map((f) => String(f.value ?? "").toLowerCase())
            .join(" "),
        };
      }),
    [items, displayFields, keyAccessor, isItemDisabled],
  );

  const gridTemplateColumns = useMemo(() => {
    const cols = displayFields.map((f) => (f.width ? `${f.width}px` : "1fr"));

    return ["24px", ...cols].join(" "); // checkbox column first
  }, [displayFields]);

  // Search
  const filtered = useMemo(() => {
    if (!showSearch || !search.trim()) return normalized;
    const q = search.toLowerCase();
    return normalized.filter((i) => i.searchText.includes(q));
  }, [normalized, search, showSearch]);

  // Grouping
  const grouped = useGroupedTree(filtered, groupBy);

  // Every item in the tree, collapsed groups included — this is select-all's scope,
  // not what is on screen. Shift-click ranges use renderedSelectableKeys below.
  const visibleItems = useMemo(() => {
    const acc: NormalizedItem<T>[] = [];
    const walk = (nodes: GroupNode<T>[]) => {
      nodes.forEach((n) => {
        if (n.type === "group") walk(n.children);
        else acc.push(n.item);
      });
    };
    walk(grouped.tree);
    return acc;
  }, [grouped.tree]);

  // What a Shift-click range may cover: rendered order, skipping collapsed groups
  // and disabled options, so a range can never reach a row the user can't see.
  const renderedSelectableKeys = useMemo(() => {
    const acc: ItemKey[] = [];
    const walk = (nodes: GroupNode<T>[]) => {
      nodes.forEach((n) => {
        if (n.type === "group") {
          if (!grouped.collapsed.has(n.id)) walk(n.children);
        } else if (!n.item.disabled) {
          acc.push(n.item.key);
        }
      });
    };
    walk(grouped.tree);
    return acc;
  }, [grouped.tree, grouped.collapsed]);

  // Only selectable items (important for select-all)
  const selectableVisibleItems = useMemo(
    () => visibleItems.filter((i) => !i.disabled),
    [visibleItems],
  );

  // Select all
  const { selectAllRef, allSelected, someSelected, setAllSelected } =
    useScopedSelectAll({
      visibleItems: selectableVisibleItems,
      selectedKeys,
      onChange,
      getKey: (i) => i.key,
    });

  const isFiltered = showSearch && search.trim().length > 0;

  const { onCheckboxClick } = useRangeSelection({
    orderedKeys: renderedSelectableKeys,
    selectedKeys,
    onChange,
    resetKey: open,
  });

  // Button text
  const selectedLabels = useMemo(() => {
    if (selectedKeys.length === 0) return [];

    const selectedSet = new Set(selectedKeys);

    return normalized
      .filter((item) => selectedSet.has(item.key))
      .map((item) => item.fields.map((f) => String(f.value ?? "")).join(" - "));
  }, [normalized, selectedKeys]);

  const noneSelected = selectedKeys.length === 0;

  const buttonText = noneSelected ? label : selectedLabels.join(", ");

  const fullTitle = noneSelected ? label : selectedLabels.join(", ");

  const searching = open && showSearch && searchInControl;

  // Outside clicks must not refocus the trigger: that would pull focus off whatever was clicked.
  const restoreFocusOnClose = useRef(false);

  const closeAndRefocus = () => {
    restoreFocusOnClose.current = true;
    setOpen(false);
  };

  useEffect(() => {
    if (open || !restoreFocusOnClose.current) return;
    restoreFocusOnClose.current = false;
    btnRef.current?.focus();
  }, [open, btnRef]);

  const handleClear = () => {
    onChange([]);
    setSearch("");
  };

  // Recursive render (group nodes)
  const renderNode = (node: GroupNode<T>): React.ReactNode => {
    if (node.type === "group") {
      const isCollapsed = grouped.collapsed.has(node.id);

      return (
        <AFS.Group key={node.id}>
          <AFS.GroupHeader>
            {/* Icon-only, so the group name is its accessible name. */}
            <button
              type="button"
              css={AFS.ExpandButton}
              title={node.label}
              aria-label={node.label}
              aria-expanded={!isCollapsed}
              onClick={() => grouped.toggle(node.id)}
            >
              <img
                src={isCollapsed ? expandIcon : collapseIcon}
                alt=""
                css={AFS.ExpandImage}
              />
            </button>
            {node.label}
          </AFS.GroupHeader>

          {!isCollapsed && node.children.map((child) => renderNode(child))}
        </AFS.Group>
      );
    }

    const item = node.item;
    const isDisabled = (item as any).disabled;

    return (
      <S.Option
        key={String(item.key)}
        css={
          isDisabled &&
          css`
            opacity: 0.4;
            pointer-events: none;
          `
        }
      >
        <AFS.Check columns={gridTemplateColumns}>
          <input
            type="checkbox"
            disabled={isDisabled}
            checked={selectedKeys.includes(item.key)}
            // Selection is handled in onClick (to read the Shift modifier);
            // onChange just satisfies React's controlled-input contract.
            onChange={() => {}}
            onClick={(e) => {
              if (isDisabled) return;
              onCheckboxClick(item.key, e);
            }}
          />
          {item.fields.map((f, i) => (
            <span key={i}>
              {f.render ? f.render(f.value, item.raw) : String(f.value)}
            </span>
          ))}
        </AFS.Check>
      </S.Option>
    );
  };

  return (
    <S.Root
      css={css`
        ${rootCss}
      `}
    >
      <S.Button
        /* A <button> may not contain the search <input>. */
        as={searching ? "div" : undefined}
        ref={btnRef as any}
        css={buttonCss}
        /* preventDefault: the wrapping <label> forwards a second click that reopens the panel. */
        onClick={(e) => {
          e.preventDefault();
          /* Anywhere but the text field closes -- the chevron included. */
          if (searching) closeAndRefocus();
          else setOpen((o) => !o);
        }}
        controlWidth={controlWidth}
        controlMaxWidth={controlMaxWidth}
        controlHeight={controlHeight}
        iconSize={iconSize}
        /* is-open: while open the trigger is a div, so :focus cannot match it. */
        className={open ? "is-open" : undefined}
      >
        <S.ButtonBody>
          {searching ? (
            <S.ControlSearch onClick={(e) => e.stopPropagation()}>
              <S.ControlSearchInput
                autoFocus
                css={selectedTextCss}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Escape" && closeAndRefocus()}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
              />
              {search && (
                <S.ClearButton
                  type="button"
                  onClick={() => setSearch("")}
                  title={getResource(resources, "ACCESS_OVERVIEW_CLEAR_SEARCH")}
                >
                  ×
                </S.ClearButton>
              )}
            </S.ControlSearch>
          ) : (
            <S.ButtonText
              isPlaceholder={noneSelected}
              title={fullTitle}
              css={noneSelected ? placeholderTextCss : selectedTextCss}
            >
              {buttonText}
              {noneSelected && requiredAsterisk && <S.RequiredMarker />}
            </S.ButtonText>
          )}
        </S.ButtonBody>

        <S.FilterIcon
          iconSize={iconSize}
          /* stopPropagation keeps the container's handler from undoing this. */
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (open) closeAndRefocus();
            else setOpen(true);
          }}
          css={iconButtonCss}
        >
          <img
            src={dropdownIconSrc ?? filterIcon}
            alt=""
            draggable={false}
            width={iconSize}
            height={iconSize}
            css={noneSelected ? iconCss : undefined}
          />
        </S.FilterIcon>
      </S.Button>

      {open && (
        <S.Panel
          css={panelCss} ref={panelRef} minW={panelMinWidth} maxW={panelMaxWidth}>
          {/* Search */}
          {showSearch && !searchInControl && (
            <S.Search>
              <S.SearchFieldWrapper>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                />
                {search && (
                  <S.ClearButton type="button" onClick={() => setSearch("")}>
                    ×
                  </S.ClearButton>
                )}
              </S.SearchFieldWrapper>
            </S.Search>
          )}

          <Row
            rootCss={css`
              display: grid;
              grid-template-columns: 1fr auto;
              align-items: center;
            `}
          >
            {/* Select all */}
            {enableSelectAll && (
              <div
                style={{
                  visibility: showSelectAll ? "visible" : "hidden",
                  display: "inline-flex",
                }}
              >
                <SelectAllToolbar
                  selectAllRef={selectAllRef}
                  allSelected={allSelected}
                  /* Scoped, because that is what the toggle acts on. */
                  indeterminate={someSelected}
                  /* Running total: an in-scope count reads "0 selected" and greys out Clear
                     once the filter moves past your picks. */
                  selectedCount={selectedKeys.length}
                  shownCount={selectableVisibleItems.length}
                  isFiltered={isFiltered}
                  onToggle={setAllSelected}
                  onClear={handleClear}
                  showClear={showClear}
                  clearLabel={clearLabel}
                />
              </div>
            )}

            {/* Expand / collapse all */}
            {groupBy && groupBy.length > 0 && (
              <S.Toolbar>
                <button
                  type="button"
                  className="action-btn"
                  onClick={grouped.expandAll}
                >
                  {getResource(resources, "ACCESS_OVERVIEW_EXPAND_ALL")}
                </button>
                <button
                  type="button"
                  className="action-btn"
                  onClick={grouped.collapseAll}
                >
                  {getResource(resources, "ACCESS_OVERVIEW_COLLAPSE_ALL")}
                </button>
              </S.Toolbar>
            )}
          </Row>

          <S.Divider />

          {/* Options */}
          <S.OptionsScroll panelMaxHeight={panelMaxHeight}>
            {grouped.tree.map((node) => renderNode(node))}
          </S.OptionsScroll>
        </S.Panel>
      )}
    </S.Root>
  );
}
